import React, { useState, useEffect, useCallback } from 'react';
import { showToast } from '../utils/toast';
import { FiEdit2, FiPlus, FiToggleLeft, FiToggleRight, FiSearch, FiPlay } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import '../styles/Cajas.css';

const EMPTY = { nombre: '', descripcion: '' };

export default function CajasPage() {
  const navigate = useNavigate();

  const [rows,        setRows]        = useState([]);
  const [almacenes,   setAlmacenes]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [search,      setSearch]      = useState('');
  const [modal,       setModal]       = useState(null);
  const [form,        setForm]        = useState(EMPTY);
  const [selAlmacenes, setSelAlmacenes] = useState([]);
  const [defaultAlm,  setDefaultAlm]  = useState(null);
  const [saving,      setSaving]      = useState(false);
  const [modalError,  setModalError]  = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [r, a] = await Promise.all([
        api.get('/api/cajas'),
        api.get('/api/cajas/almacenes'),
      ]);
      setRows(r.data.data || []);
      setAlmacenes(a.data.data || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = search.trim()
    ? rows.filter(r => r.nombre.toLowerCase().includes(search.toLowerCase()))
    : rows;

  const openNew = () => {
    setForm(EMPTY);
    setSelAlmacenes([]);
    setDefaultAlm(null);
    setModalError('');
    setModal({ mode: 'new' });
  };

  const openEdit = (r) => {
    setForm({ nombre: r.nombre, descripcion: r.descripcion || '' });
    const ids = r.almacenes?.map(a => a.almacen_id) || [];
    setSelAlmacenes(ids);
    setDefaultAlm(r.almacenes?.find(a => a.es_default)?.almacen_id || ids[0] || null);
    setModalError('');
    setModal({ mode: 'edit', id: r.id });
  };

  const toggleAlmacen = (id) => {
    setSelAlmacenes(prev => {
      if (prev.includes(id)) {
        const next = prev.filter(x => x !== id);
        if (defaultAlm === id) setDefaultAlm(next[0] || null);
        return next;
      }
      const next = [...prev, id];
      if (!defaultAlm) setDefaultAlm(id);
      return next;
    });
  };

  const handleSave = async () => {
    if (!form.nombre.trim()) { setModalError('El nombre es requerido.'); return; }
    if (selAlmacenes.length === 0) { setModalError('Selecciona al menos un almacén.'); return; }
    setSaving(true); setModalError('');
    try {
      const payload = {
        nombre: form.nombre,
        descripcion: form.descripcion,
        almacen_ids: selAlmacenes,
        default_almacen_id: defaultAlm,
      };
      if (modal.mode === 'new') await api.post('/api/cajas', payload);
      else                      await api.put(`/api/cajas/${modal.id}`, payload);
      await load();
      setModal(null);
    } catch (e) {
      setModalError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id) => {
    try { await api.patch(`/api/cajas/${id}/activo`); await load(); }
    catch (e) { showToast(e.response?.data?.error || 'Error'); }
  };

  return (
    <div className="cj-page">
      {/* Toolbar */}
      <div className="cj-toolbar">
        <button className="cj-btn-nuevo" onClick={openNew}>
          <FiPlus size={13} /> Nueva Caja
        </button>
        <div className="cj-search-wrap">
          <FiSearch size={13} style={{ color: '#94a3b8' }} />
          <input
            className="cj-search"
            placeholder="Buscar por nombre..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <span className="cj-count">{filtered.length} caja(s)</span>
      </div>

      {/* Table */}
      <div className="cj-table-wrap">
        {loading ? (
          <div className="cj-loading">Cargando...</div>
        ) : error ? (
          <div className="cj-error">{error}</div>
        ) : (
          <table className="cj-table">
            <thead>
              <tr>
                <th className="cj-th" style={{ width: 180 }}>Nombre</th>
                <th className="cj-th">Descripción</th>
                <th className="cj-th" style={{ width: 260 }}>Almacenes vinculados</th>
                <th className="cj-th cj-th-center" style={{ width: 90 }}>Estado</th>
                <th className="cj-th cj-th-center" style={{ width: 110 }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="cj-empty">Sin cajas registradas</td>
                </tr>
              ) : filtered.map((r, i) => (
                <tr key={r.id} className={i % 2 === 0 ? 'cj-tr-even' : 'cj-tr-odd'}>
                  <td className="cj-td">
                    <div className="cj-nombre">{r.nombre}</div>
                    {r.descripcion && <div className="cj-desc">{r.descripcion}</div>}
                  </td>
                  <td className="cj-td" style={{ color: '#64748b', fontSize: 12 }}>
                    {r.descripcion || <span style={{ color: '#d1d5db' }}>—</span>}
                  </td>
                  <td className="cj-td">
                    <div className="cj-chips">
                      {(r.almacenes || []).length === 0 ? (
                        <span style={{ color: '#d1d5db', fontSize: 12 }}>Sin almacenes</span>
                      ) : (r.almacenes || []).map(a => (
                        <span
                          key={a.almacen_id}
                          className={`cj-chip ${a.es_default ? 'cj-chip-default' : ''}`}
                          title={a.es_default ? 'Almacén por defecto' : ''}
                        >
                          {a.almacen_nombre}
                          {a.es_default && ' ★'}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="cj-td cj-td-center">
                    <span className={`cj-badge ${r.activo ? 'cj-badge-active' : 'cj-badge-inactive'}`}>
                      {r.activo ? 'ACTIVO' : 'INACTIVO'}
                    </span>
                  </td>
                  <td className="cj-td cj-td-center">
                    <div className="ia-actions">
                      <button
                        className="ia-btn ia-btn--edit"
                        title="Editar"
                        onClick={() => openEdit(r)}
                      ><FiEdit2 size={15} /></button>
                      <button
                        className={`ia-btn ${r.activo ? 'ia-btn--on' : 'ia-btn--off'}`}
                        title={r.activo ? 'Desactivar' : 'Activar'}
                        onClick={() => handleToggle(r.id)}
                      >
                        {r.activo ? <FiToggleLeft size={15} /> : <FiToggleRight size={15} />}
                      </button>
                      <button
                        className="ia-btn ia-btn--open"
                        title="Operar caja"
                        onClick={() => navigate('/caja/apertura')}
                      ><FiPlay size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="cj-footer">
        Todos los Derechos reservados Devel@per F@xy &nbsp;·&nbsp; www.developerfoxy.com
      </div>

      {/* Modal */}
      {modal && (
        <div className="cj-overlay">
          <div className="cj-modal" onClick={e => e.stopPropagation()}>
            <div className="cj-modal-header">
              <span>{modal.mode === 'new' ? 'Nueva Caja' : 'Editar Caja'}</span>
              <button className="cj-modal-close" onClick={() => setModal(null)}>×</button>
            </div>

            <div className="cj-modal-body">
              <div className="cj-row cj-row-1">
                <div className="cj-field">
                  <label className="cj-label">Nombre *</label>
                  <input
                    className="cj-input"
                    value={form.nombre}
                    onChange={e => { setModalError(''); setForm(p => ({ ...p, nombre: e.target.value })); }}
                    placeholder="Ej: CAJA PRINCIPAL"
                    autoFocus
                  />
                </div>
              </div>

              <div className="cj-row cj-row-1">
                <div className="cj-field">
                  <label className="cj-label">Descripción</label>
                  <input
                    className="cj-input"
                    value={form.descripcion}
                    onChange={e => setForm(p => ({ ...p, descripcion: e.target.value }))}
                    placeholder="Descripción opcional"
                  />
                </div>
              </div>

              <div className="cj-field">
                <label className="cj-label">
                  Almacenes vinculados *
                  <span style={{ marginLeft: 6, color: '#94a3b8', fontWeight: 400, textTransform: 'none', fontSize: 11 }}>
                    — marca ★ para el almacén por defecto
                  </span>
                </label>
                {almacenes.length === 0 ? (
                  <p style={{ fontSize: 12, color: '#dc2626' }}>No hay almacenes activos. Crea uno primero.</p>
                ) : (
                  <div className="cj-almacen-list">
                    {almacenes.map(a => {
                      const checked = selAlmacenes.includes(a.id);
                      const isDefault = defaultAlm === a.id;
                      return (
                        <div key={a.id} className="cj-almacen-row">
                          <input
                            type="checkbox"
                            className="cj-almacen-check"
                            checked={checked}
                            onChange={() => toggleAlmacen(a.id)}
                          />
                          <span className="cj-almacen-name">{a.nombre}</span>
                          {checked && (
                            <>
                              <input
                                type="radio"
                                className="cj-almacen-default-radio"
                                name="default_alm"
                                checked={isDefault}
                                onChange={() => setDefaultAlm(a.id)}
                                title="Almacén por defecto"
                              />
                              <span className="cj-almacen-default-label">por defecto</span>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {modalError && <div className="cj-modal-error">{modalError}</div>}

            <div className="cj-modal-footer">
              <button className="cj-btn-save" onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : modal.mode === 'new' ? 'Guardar' : 'Actualizar'}
              </button>
              <button className="cj-btn-cancel" onClick={() => setModal(null)}>× Salir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
